import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

export interface AlertContextType {
	alert: alertData;
	updateAlert: (a: Partial<alertData>) => void;
	clearAlert: () => void;

	unsavedChangesAlert: (onConfirmFn: () => void) => void;
}

const defaultContextValues = {
	alert: {} as alertData,
} as AlertContextType;
interface AlertContextProps {
	children: ReactNode;
}

export const AlertContext = createContext<AlertContextType>(defaultContextValues);
export const useAlertContext = () => useContext(AlertContext);
export function AlertProvider(props: AlertContextProps) {
	const { children } = props;
	const [alert, setAlert] = useState({} as alertData);

	const updateAlert = useCallback((a: Partial<alertData>) => setAlert(a), [setAlert]);
	const clearAlert = useCallback(() => setAlert({} as alertData), [setAlert]);
	const unsavedChangesAlert = useCallback(
		(onConfirmFn: () => void) => {
			updateAlert({
				titleText: "Pending Changes",
				message: "You have unsaved changes. Are you sure you would like to continue?",
				confirmText: "Continue",
				cancelText: "Go Back",
				onConfirm: () => {
					onConfirmFn();
					clearAlert();
				},
				onCancel: clearAlert,
			});
		},
		[updateAlert, clearAlert],
	);

	const all = useMemo(
		() => ({ alert, updateAlert, clearAlert, unsavedChangesAlert }),
		[alert, updateAlert, clearAlert, unsavedChangesAlert],
	);

	return <AlertContext.Provider value={all}>{children}</AlertContext.Provider>;
}

export interface alertData {
	message?: string;
	body?: JSX.Element;
	onConfirm?: () => void;
	onCancel?: () => void;
	onReject?: () => void;
	confirmText?: string;
	confirmDisabled?: boolean;
	cancelText?: string;
	rejectText?: string;
	titleText?: string;
	className?: string;
	type?: "warning" | "notify";
	size?: "default" | "sm" | "lg" | "auto";
}
